<template>
  <b-modal
    id="modal-upload-employee-photo"
    ref="modalUploadEmployeePhoto"
    size="md"
    centered
    :title="isUploading ? 'Uploading...' : $t('rfidAndFacerecognition.upload.title')"
  >
    <b-card class="mc-card mc-card__setting mc-card__setting--upload-employee-photo">
      <div class="d-flex justify-content-center align-items-center">
        <img :src="UploadGuideline" alt="Upload Guideline" style="max-width: 100%; height: auto" />
      </div>
      <p class="text-muted text-center mt-3">
        {{ $t('rfidAndFacerecognition.upload.subtitle') }}
      </p>
      <div v-if="!isUploading" class="upload-container">
        <FileUploader
          ref="fileUploader"
          :data-me="`master-employee-modal-dropzone-upload-${employee?.userId || 'new'}`"
          :title="$t('rfidAndFacerecognition.upload.chooseOrDropFile')"
          :subtitle="$t('rfidAndFacerecognition.upload.fileType')"
          :buttonText="$t('rfidAndFacerecognition.upload.chooseFile')"
          accept=".jpg,.png"
          :maxSize="2 * 1024 * 1024"
          @selected="handleSelectedFile"
          @error="handleError"
        />
      </div>
      <div v-else class="upload-progress-view">
        <p class="file-name text-center">{{ file ? file.name : '-' }}</p>
        <b-row>
          <b-col sm="12">
            <b-progress
              :data-me="`master-employee-modal-dropzone-progress-${employee?.userId || 'new'}`"
              :value="uploadProgress"
              variant="primary"
              class="mt-2 custom-progress"
            ></b-progress>
          </b-col>
        </b-row>

        <div class="d-flex justify-content-between align-items-center">
          <span class="file-size text-muted">
            {{ formatSize(uploadedSize) }} / {{ formatSize(totalSize) }}
          </span>
          <div class="d-flex align-items-center">
            <span class="progress-percentage mr-3">{{ uploadProgress }} %</span>
          </div>
        </div>
      </div>
    </b-card>
    <template #modal-footer="{ cancel }">
      <b-row class="mc-modal__filter-footer mx-0">
        <b-col sm="12" md="12" class="mc-modal__filter-footer-btn mt-0">
          <McButton
            :data-me="`master-employee-btn-close-modal-upload-${employee?.userId || 'new'}`"
            :disabled="isUploading"
            variant="outline-primary"
            class="me-2"
            :text="$t('global.action.close')"
            @click="cancel()"
          />
          <McButton
            :data-me="`master-employee-btn-upload-${employee?.userId || 'new'}`"
            variant="primary"
            :disabled="!file || isUploading"
            :text="$t('global.action.apply')"
            @click="processFile"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import FileUploader from '@/router/views/master/rfid-facerecognition/components/FileUploader.vue'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import UploadGuideline from '@/assets/images/face-recognition/upload-guideline.jpg'

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png']
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']

export default {
  name: 'ModalUploadEmployeePhoto',
  components: {
    McButton,
    FileUploader,
  },
  mixins: [Helper],
  data() {
    return {
      isUploading: false,
      UploadGuideline,
      file: null,
      fileExt: null,
      employee: null,
      uploadProgress: 0,
      uploadedSize: 0,
      totalSize: 0,
      uploadInterval: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },
  beforeDestroy() {
    if (this.uploadInterval) {
      clearInterval(this.uploadInterval)
    }
  },
  methods: {
    show(employee, file = null) {
      this.resetState()
      this.employee = employee
      this.$bvModal.show('modal-upload-employee-photo')
      if (file) {
        this.file = file
        this.fileExt = this.getFileExtension(file)
        this.$nextTick(() => {
          this.$refs.fileUploader?.validate(file)
        })
      }
    },
    hide() {
      this.$bvModal.hide('modal-upload-employee-photo')
    },
    processFile() {
      if (!this.file) return

      if (!this.validateFileType(this.file)) {
        this.$mcToast.error(
          this.$t('global.alert.text.invalidFileType', {
            extensions: ALLOWED_EXTENSIONS.join(', '),
          })
        )
        return
      }

      this.totalSize = this.file.size
      this.uploadedSize = 0
      this.uploadProgress = 0
      this.isUploading = true

      this.performUpload()
    },

    validateFileType(file) {
      const mimeValid = ALLOWED_FILE_TYPES.includes(file.type)
      const ext = this.getFileExtension(file)
      const extValid = ALLOWED_EXTENSIONS.includes(ext.toLowerCase())
      return mimeValid && extValid
    },

    getFileExtension(file) {
      return file.name.split('.').pop() ?? ''
    },

    handleError(msg) {
      this.$mcToast.error(msg)
    },
    async performUpload() {
      this.isUploading = true
      this.uploadProgress = 0
      this.uploadedSize = 0
      this.totalSize = this.file.size

      this.simulateProgress()

      try {
        const base64Photo = await this.fileToBase64(this.file)
        const splitBase64Photo = base64Photo.split(',')[1]

        clearInterval(this.uploadInterval)
        this.uploadProgress = 100
        this.uploadedSize = this.totalSize

        // Emit success to parent so it can update the local state/preview
        // Note: We no longer call the UPDATE_EMPLOYEE mutation here
        this.$emit('success', {
          photo: splitBase64Photo,
          photoExt: this.fileExt,
          fileName: this.file.name,
          previewUrl: URL.createObjectURL(this.file),
        })

        setTimeout(() => {
          this.hide()
          this.resetState()
        }, 800)
      } catch (error) {
        clearInterval(this.uploadInterval)
        this.file = null
        this.isUploading = false
        this.$mcToast.error(error?.message || this.$t('global.alert.text.uploadFailed'))
      }
    },
    simulateProgress() {
      this.uploadInterval = setInterval(() => {
        if (this.uploadProgress < 90) {
          this.uploadProgress += 5
          this.uploadedSize = (this.uploadProgress / 100) * this.totalSize
        }
      }, 200)
    },
    resetState() {
      if (this.uploadInterval) {
        clearInterval(this.uploadInterval)
      }
      this.isUploading = false
      this.file = null
      this.fileExt = null
      this.uploadProgress = 0
      this.uploadedSize = 0
      this.totalSize = 0
      this.employee = null
      this.uploadInterval = null
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },
    formatSize(bytes) {
      if (bytes === 0) return '0 MB'
      const mb = bytes / (1024 * 1024)
      return mb.toFixed(1) + ' MB'
    },
    handleSelectedFile(file) {
      this.file = file
      this.fileExt = this.getFileExtension(file)
    },
  },
}
</script>

<style lang="scss" scoped></style>
